import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getChronicleBySlug } from "../../services/chroniclesApi";
import ChronicleArticleBody, {
  getChronicleImageUrl,
  formatChronicleDate,
} from "../../utils/chronicleContentRenderer";
import { buildCanonical, SITE_ORIGIN } from "../../constants/siteSeo";
import { setPageMeta, clearPageMeta } from "../../utils/setPageMeta";
import "./chronicles.css";

const DETAIL_DESC_FALLBACK =
  "Official archive record from Treydora and the Rise of Koraxos ecosystem.";

function toAbsoluteUrl(url) {
  if (!url || typeof url !== "string") return "";
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (typeof window === "undefined") return u;
  const pub = typeof process.env.PUBLIC_URL === "string" ? process.env.PUBLIC_URL : "";
  const base = `${window.location.origin}${pub === "/" ? "" : pub || ""}`;
  if (u.startsWith("/")) return `${base.replace(/\/$/, "")}${u}`;
  return u;
}

export default function ChronicleDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      setPost(null);
      try {
        const raw = slug ? decodeURIComponent(slug) : "";
        const data = await getChronicleBySlug(raw);
        if (!cancelled) setPost(data || null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const titleBase = String(post.seoTitle || post.title || "Chronicle").trim();
    const desc = String(post.seoDescription || post.excerpt || DETAIL_DESC_FALLBACK).trim();
    const ogImageRaw =
      post.socialShareImage || post.heroImage || post.featuredImage || post.thumbnailImage || "";
    const ogImage = toAbsoluteUrl(typeof ogImageRaw === "string" ? ogImageRaw : "");
    const slugPath = post.slug ? `/chronicles/${post.slug}` : "/chronicles";
    setPageMeta({
      title: `${titleBase} | Treydora`,
      description: desc,
      ogTitle: titleBase,
      ogDescription: desc,
      ogImage: ogImage || undefined,
      ogType: "article",
      canonicalUrl:
        typeof post.canonicalUrl === "string" && post.canonicalUrl.trim()
          ? post.canonicalUrl.trim()
          : buildCanonical(slugPath),
      ogUrl: buildCanonical(slugPath),
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: titleBase,
        description: desc,
        url: `${SITE_ORIGIN}${slugPath}`,
        image: ogImage || undefined,
        publisher: { "@type": "Organization", name: "Treydora Productions" },
      },
    });
    return () => clearPageMeta();
  }, [post]);

  if (loading) {
    return (
      <div className="chr">
        <div className="chr-inner">
          <div className="chr-state" role="status" style={{ marginTop: 48 }}>
            <p className="chr-state__title chr-serif">Opening the archive…</p>
            <p className="chr-state__text">Retrieving this transmission.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="chr">
        <div className="chr-inner">
          <div className="chr-state" role="alert" style={{ marginTop: 48 }}>
            <p className="chr-state__title chr-serif">The archive could not be reached.</p>
            <p className="chr-state__text">
              This record is missing or the vault could not be opened. Return to the archive index and try again.
            </p>
            <Link to="/chronicles" className="chr-btn-primary" style={{ marginTop: 22 }}>
              Back to Chronicles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const heroSrc = getChronicleImageUrl(post);
  const related = Array.isArray(post.relatedArticles) ? post.relatedArticles : [];

  return (
    <div className="chr">
      <div className="chr-detail-hero" aria-hidden={false}>
        <img src={heroSrc} alt="" />
      </div>

      <div className="chr-detail-wrap">
        <article className="chr-detail-panel">
          {post.category ? <span className="chr-badge">{post.category}</span> : null}
          <h1 className="chr-detail__title">{post.title}</h1>
          {post.subtitle ? <p className="chr-detail__subtitle">{post.subtitle}</p> : null}
          {post.excerpt ? <p className="chr-detail__excerpt">{post.excerpt}</p> : null}

          <div className="chr-meta-row chr-detail__actions">
            <span>{formatChronicleDate(post.publishedAt)}</span>
            {post.authorName ? <span>{post.authorName}</span> : null}
            {post.readingTimeMinutes != null ? <span>{post.readingTimeMinutes} min read</span> : null}
          </div>

          {Array.isArray(post.tags) && post.tags.length ? (
            <div className="chr-tags chr-detail__tags">
              {post.tags.map((t) => (
                <span key={t} className="chr-tag">
                  {t}
                </span>
              ))}
            </div>
          ) : null}

          <div className="chr-detail__actions">
            <Link to="/chronicles" className="chr-btn-ghost">
              ← Back to Chronicles
            </Link>
          </div>

          <ChronicleArticleBody post={post} />

          {related.length ? (
            <section className="chr-related" aria-labelledby="chr-related-title">
              <h2 id="chr-related-title" className="chr-related__title">
                Related records
              </h2>
              <ul className="chr-related__list">
                {related.map((r) => (
                  <li key={r._id || r.slug}>
                    <Link to={`/chronicles/${encodeURIComponent(r.slug)}`}>{r.title}</Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </article>
      </div>
    </div>
  );
}
